import { Observable } from 'rxjs';

import { SnAlarm, SnCamera, SnCameraDrone, SnMapView } from '@lib/api/entities';
import { EsriMap } from '@lib/geo/esri';
import { GoogleMap } from '@lib/geo/google/map';
import { FsMap } from '@lib/geo/map';
import { FsLvtCamera } from '@lib/lvt';

import { IFsMap } from '../types';

export interface MapViewData {
  longitude: number;
  latitude: number;
  zoom: number;
  id: string;
}

export abstract class FsMapDockedViewServiceProvider<TMap extends IFsMap> {
  abstract get telestrationView(): boolean;
  abstract get telestrationMode(): string;
  abstract readonly geospatialProvider: string;
  abstract readonly id: string;
  abstract get view$(): Observable<SnMapView>;
  abstract get views$(): Observable<SnMapView[]>;
  abstract get removeMarker$(): Observable<any>;
  abstract get cameras$(): Observable<SnCamera[]>;
  abstract get drones$(): Observable<SnCameraDrone[]>;
  abstract get playbackCameras$(): Observable<SnCamera[]>;
  abstract get cameraExistsAlready$(): Observable<SnCamera>;
  abstract get initPlayback$(): Observable<SnMapView>;
  abstract get removedTab$(): Observable<SnMapView>;
  abstract get switched$(): Observable<void>;
  abstract get mapUpdated$(): Observable<any>;
  abstract get updateTab$(): Observable<SnMapView>;
  abstract get featureMouseOver$(): Observable<any>;
  abstract get featureMouseOut$(): Observable<any>;
  abstract get skydioUpdated$(): Observable<any>;
  abstract get coreMobileUpdated$(): Observable<any>;

  abstract QUERY_PARAM_DOCKED_VIEW: string;

  // variables
  abstract get map(): TMap;
  abstract get currentView(): SnMapView;
  abstract get currentViewIndex(): number;
  abstract get cameras(): SnCamera[];
  abstract get drones(): SnCameraDrone[];
  abstract get lvts(): FsLvtCamera[];
  abstract get playbackCams(): SnCamera[];
  abstract get views(): SnMapView[];
  abstract get loaded(): boolean;

  // functions
  abstract setClosedAlarm(alarm: SnAlarm): void;
  abstract getClosedAlarm(): Observable<SnAlarm>;
  abstract onMapLoaded(map: TMap): void;
  abstract createViewForNearbyCameras(data: MapViewData, radius: number): void;
  abstract createViewForCameras(data: MapViewData, cameras: SnCamera[], zoomLevel: number): void;
  abstract featureMouseOver(feature: any): void;
  abstract featureMouseOut(feature: any): void;
  abstract skydioUpdate(feature: any): void;
  abstract coreMobileUpdate(feature: any): void;
  abstract setView(mapView?: SnMapView, controls?: boolean): void;
  abstract setViewByCitizenIncidentId(citizenIncidentId: string): Observable<SnMapView | undefined>;
  abstract backToMap(): void;
  abstract goToSavedMap(): void;
  abstract setViews(mapViews: SnMapView[]): void;
  abstract updateTab(view: SnMapView): void;
  abstract addCamera(camera: SnCamera): void;
  abstract addDrone(drone: SnCameraDrone): void;
  abstract dockCameras(cameras: SnCamera[]): void;
  abstract dockDrone(drone: SnCameraDrone[]): void;
  abstract removeCamera(camera: SnCamera): void;
  abstract removeDrone(drone: SnCameraDrone): void;
  abstract removeAllCameras(): void;
  abstract viewPlayback(camera: SnCamera): void;
  abstract removePlayback(camera: SnCamera): void;
  abstract updateView(
    lon: number,
    lat: number,
    zoomLevel: number,
    metaData?: any,
  ): Observable<SnMapView>;
  abstract deleteView(
    view: SnMapView,
    options?: {
      message?: string;
      archiveButtonText?: string;
      deleteButtonText?: string;
      successMessage?: string;
      errorMessage?: string;
      disableAltBtn?: boolean;
    },
  ): Observable<any>;
  abstract deleteViews(view: SnMapView): Observable<any>;
  abstract archiveViews(view: SnMapView): Observable<any>;
  abstract updateMapView(): void;
  abstract silentUpdate(): Observable<SnMapView>;
  abstract removeTab(tab: SnMapView): void;
  abstract setTabNumber(tabAmount: number): void;
  abstract dockModemCameras(feature: any): void;
  abstract createModemGrid(applianceIds: string[]): void;
  abstract clearViewCameras(): void;
  abstract setCurrentViewIndex(index: number): void;
}
